/**
 * UserMemberships hooks using React Relay
 *
 * These hooks provide access to user membership data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useUserMembershipsRelayQuery as UseUserMembershipsRelayQuery,
  useUserMembershipsRelayQuery$variables,
  UserMembershipFilterInput,
  UserMembershipSortInput,
} from './__generated__/useUserMembershipsRelayQuery.graphql';
import type { useUserMembershipsRelayDirectQuery as UseUserMembershipsRelayDirectQuery } from './__generated__/useUserMembershipsRelayDirectQuery.graphql';
import type { useUserMembershipsRelayByIdQuery } from './__generated__/useUserMembershipsRelayByIdQuery.graphql';
import type { useUserMembershipsRelayPaginationFragment$key } from './__generated__/useUserMembershipsRelayPaginationFragment.graphql';
import type { PerkGraphQL, MembershipPriceGraphQL } from './useMembershipsRelay';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for user memberships list
 */
const UserMembershipsListPaginationFragment = graphql`
  fragment useUserMembershipsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useUserMembershipsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "UserMembershipFilterInput" }
    sort: { type: "[UserMembershipSortInput!]" }
    search: { type: "String" }
  ) {
    listUserMemberships(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      search: $search
    )
      @connection(
        key: "useUserMembershipsRelayPaginationFragment_listUserMemberships"
        filters: ["filter", "sort", "search"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          createdOn
          approvedOn
          canceledOn
          expiresOn
          rejectedOn
          dismissedOn
          isActive
          isApproved
          isCanceled
          isExpired
          isPending
          isRejected
          priority
          renewedTimes
          renewedDuration
          paidRenewals
          freeRenewals
          failedRenewalAttempts
          lastCharged
          membershipOldId
          membershipPriceId
          systemId
          organizationId
          userId
          userOldId
          email
          data
          rejectionReason
          adminCancellationReason
          cancelationType
          canceledByAdmin
          canceledByAdminUser {
            id
            email
            firstName
            lastName
          }
          membershipPrice {
            id
            price
            priority
            autoRenews
            renewalInterval
            termStartsOn
            termEndsOn
            minutes
            unlocks
            name
          }
          user {
            id
            phone
            firstName
            lastName
            fullName
            email
          }
          membership {
            id
            oldId
            name
            authType
            authExpiration
            activeOn
            archivedOn
            brandingColor
            logoImageId
            data
            perks {
              id
              oldId
              freeTime
              discount
              moneyOff
              waiveBaseRate
              noFlex
              systemOldId
              locationId
              system {
                id
                name
                currencyCode
                mobilityType
              }
              location {
                id
                name
              }
            }
            prices {
              id
              price
              priority
              autoRenews
              renewalInterval
              termStartsOn
              termEndsOn
              minutes
              unlocks
              name
            }
          }
        }
      }
    }
  }
`;

/**
 * Query for paginated user memberships list (uses fragment for Suspense hooks)
 */
const UserMembershipsListQuery = graphql`
  query useUserMembershipsRelayQuery(
    $first: Int
    $after: String
    $filter: UserMembershipFilterInput
    $sort: [UserMembershipSortInput!]
    $search: String
  ) {
    ...useUserMembershipsRelayPaginationFragment
      @arguments(
        first: $first
        after: $after
        filter: $filter
        sort: $sort
        search: $search
      )
  }
`;

/**
 * Direct query for user memberships list (for non-Suspense fetchQuery usage)
 */
const UserMembershipsListDirectQuery = graphql`
  query useUserMembershipsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: UserMembershipFilterInput
    $sort: [UserMembershipSortInput!]
    $search: String
  ) {
    listUserMemberships(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      search: $search
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          createdOn
          approvedOn
          canceledOn
          expiresOn
          rejectedOn
          dismissedOn
          isActive
          isApproved
          isCanceled
          isExpired
          isPending
          isRejected
          priority
          renewedTimes
          renewedDuration
          paidRenewals
          freeRenewals
          failedRenewalAttempts
          lastCharged
          membershipOldId
          membershipPriceId
          systemId
          organizationId
          userId
          userOldId
          email
          data
          rejectionReason
          adminCancellationReason
          cancelationType
          canceledByAdmin
          canceledByAdminUser {
            id
            email
            firstName
            lastName
          }
          membershipPrice {
            id
            price
            priority
            autoRenews
            renewalInterval
            termStartsOn
            termEndsOn
            minutes
            unlocks
            name
          }
          user {
            id
            phone
            firstName
            lastName
            fullName
            email
          }
          membership {
            id
            oldId
            name
            authType
            authExpiration
            activeOn
            archivedOn
            brandingColor
            logoImageId
            data
            perks {
              id
              oldId
              freeTime
              discount
              moneyOff
              waiveBaseRate
              noFlex
              systemOldId
              locationId
              system {
                id
                name
                currencyCode
                mobilityType
              }
              location {
                id
                name
              }
            }
            prices {
              id
              price
              priority
              autoRenews
              renewalInterval
              termStartsOn
              termEndsOn
              minutes
              unlocks
              name
            }
          }
        }
      }
    }
  }
`;

/**
 * Query for single user membership by ID
 */
const UserMembershipByIdQuery = graphql`
  query useUserMembershipsRelayByIdQuery($id: ID!) {
    getUserMembership(id: $id) {
      id
      oldId
      createdOn
      approvedOn
      canceledOn
      expiresOn
      rejectedOn
      dismissedOn
      isActive
      isApproved
      isCanceled
      isExpired
      isPending
      isRejected
      priority
      renewedTimes
      renewedDuration
      paidRenewals
      freeRenewals
      failedRenewalAttempts
      lastCharged
      membershipOldId
      membershipPriceId
      systemId
      organizationId
      userId
      userOldId
      email
      data
      rejectionReason
      adminCancellationReason
      cancelationType
      canceledByAdmin
      canceledByAdminUser {
        id
        email
        firstName
        lastName
      }
      membershipPrice {
        id
        price
        priority
        autoRenews
        renewalInterval
        termStartsOn
        termEndsOn
        minutes
        unlocks
        name
      }
      user {
        id
        phone
        firstName
        lastName
        fullName
        email
      }
      membership {
        id
        oldId
        name
        authType
        authExpiration
        activeOn
        archivedOn
        brandingColor
        logoImageId
        data
        perks {
          id
          oldId
          freeTime
          discount
          moneyOff
          waiveBaseRate
          noFlex
          systemOldId
          locationId
          system {
            id
            name
            currencyCode
            mobilityType
          }
          location {
            id
            name
          }
        }
        prices {
          id
          price
          priority
          autoRenews
          renewalInterval
          termStartsOn
          termEndsOn
          minutes
          unlocks
          name
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { UserMembershipFilterInput, UserMembershipSortInput };

/**
 * User type from GraphQL (nested in UserMembership)
 */
export interface UserMembershipUserGraphQL {
  id: string;
  phone: string;
  firstName: string;
  lastName: string;
  fullName?: string | null;
  email?: string | null;
}

/**
 * Simplified membership type for user membership context
 */
export interface UserMembershipMembershipGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  authType?: string | null;
  authExpiration?: string | null;
  activeOn?: string | null;
  archivedOn?: string | null;
  brandingColor: string;
  logoImageId?: number | null;
  // Json scalar: arrives as an already-parsed object in the response
  data?: unknown;
  perks: readonly PerkGraphQL[];
  prices: readonly MembershipPriceGraphQL[];
}

/**
 * UserMembership type from GraphQL - matches the fragment fields
 */
/**
 * AdminUser type from GraphQL (nested in UserMembership for canceledByAdminUser)
 */
export interface AdminUserGraphQL {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface UserMembershipGraphQL {
  id: string;
  oldId?: string | null;
  createdOn: string;
  approvedOn?: string | null;
  canceledOn?: string | null;
  expiresOn?: string | null;
  rejectedOn?: string | null;
  dismissedOn?: string | null;
  isActive?: boolean | null;
  isApproved?: boolean | null;
  isCanceled?: boolean | null;
  isExpired?: boolean | null;
  isPending?: boolean | null;
  isRejected?: boolean | null;
  priority: number;
  renewedTimes: number;
  renewedDuration?: string | null;
  paidRenewals: number;
  freeRenewals: number;
  failedRenewalAttempts?: number | null;
  lastCharged?: string | null;
  membershipOldId: string;
  membershipPriceId?: string | null;
  systemId?: string | null;
  organizationId?: string | null;
  userId?: string | null;
  userOldId: string;
  email?: string | null;
  // Json scalar: arrives as an already-parsed object in the response
  data?: unknown;
  rejectionReason?: string | null;
  adminCancellationReason?: string | null;
  cancelationType?: string | null;
  canceledByAdmin?: string | null;
  canceledByAdminUser?: AdminUserGraphQL | null;
  membershipPrice?: MembershipPriceGraphQL | null;
  user?: UserMembershipUserGraphQL | null;
  membership?: UserMembershipMembershipGraphQL | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface UserMembershipQueryFilter {
  userMembershipId?: string;
  membershipId?: string;
  membershipOldId?: string;
  userId?: string;
  systemId?: string;
  organizationId?: string;
  // Status filters
  pending?: boolean; // server-computed isPending: awaiting admin approval
  approved?: boolean; // approvedOn is not null
  rejected?: boolean; // rejectedOn is not null
  canceled?: boolean; // canceledOn is not null
  expired?: boolean; // expiresOn is not null and expiresOn < now
  active?: boolean; // approvedOn is not null and canceledOn is null and (expiresOn is null or expiresOn > now)
  // The rows the legacy archive_membership cancels or rejects: no canceledOn,
  // no rejectedOn, not expired. Covers approved riders and every kind of
  // pending one (admin approval or email verification).
  notEnded?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching user memberships list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 */
export function useUserMembershipsRelay(
  filter?: UserMembershipQueryFilter,
  options?: {
    limit?: number;
    sort?: UserMembershipSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const variables: useUserMembershipsRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseUserMembershipsRelayQuery>(
    UserMembershipsListQuery,
    variables
  );

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseUserMembershipsRelayQuery,
    useUserMembershipsRelayPaginationFragment$key
  >(UserMembershipsListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes =
    data.listUserMemberships?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as UserMembershipGraphQL[],
    count: data.listUserMemberships?.count,
    pageInfo: data.listUserMemberships?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for fetching a single user membership by ID using Relay
 *
 * Uses Suspense - wrap with Suspense boundary
 */
export function useUserMembershipByIdRelay(userMembershipId: string) {
  const data = useLazyLoadQuery<useUserMembershipsRelayByIdQuery>(
    UserMembershipByIdQuery,
    {
      id: userMembershipId,
    }
  );

  return data.getUserMembership as UserMembershipGraphQL | null;
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for user memberships list
 */
export function useUserMembershipsRelayLoader(initialFilter?: UserMembershipQueryFilter) {
  const [queryRef, loadQuery, disposeQuery] =
    useQueryLoader<UseUserMembershipsRelayQuery>(
      UserMembershipsListQuery,
      initialFilter
        ? {
            first: 100,
            filter: buildRelayFilter(initialFilter),
            sort: [{ field: 'CREATED_ON', order: 'DESC' }],
          }
        : undefined
    );

  const load = (
    filter?: UserMembershipQueryFilter,
    options?: { limit?: number; sort?: UserMembershipSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [
      { field: 'CREATED_ON' as const, order: 'DESC' as const },
    ];

    loadQuery({
      first: limit,
      filter: buildRelayFilter(filter),
      sort,
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded user memberships query
 */
export function useUserMembershipsRelayPreloaded(
  queryRef: PreloadedQuery<UseUserMembershipsRelayQuery>
) {
  const queryData = usePreloadedQuery<UseUserMembershipsRelayQuery>(
    UserMembershipsListQuery,
    queryRef
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseUserMembershipsRelayQuery,
    useUserMembershipsRelayPaginationFragment$key
  >(UserMembershipsListPaginationFragment, queryData);

  const nodes =
    data.listUserMemberships?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as UserMembershipGraphQL[],
    count: data.listUserMemberships?.count,
    pageInfo: data.listUserMemberships?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for user memberships data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 */
export function useUserMembershipsRelayQuery(
  filter?: UserMembershipQueryFilter,
  options?: {
    limit?: number;
    sort?: UserMembershipSortInput[];
    enabled?: boolean;
    after?: string | null;
    search?: string;
  }
) {
  const [data, setData] = useState<UserMembershipGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;
  const after = options?.after || undefined;
  const search = options?.search || undefined;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildRelayFilter(filter);
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (after) {
          variables.after = after;
        }
        if (builtFilter) {
          variables.filter = builtFilter;
        }
        if (search) {
          variables.search = search;
        }

        const result = await fetchQuery<UseUserMembershipsRelayDirectQuery>(
          relayEnvironment,
          UserMembershipsListDirectQuery,
          variables as useUserMembershipsRelayQuery$variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listUserMemberships?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as UserMembershipGraphQL[]);
          setCount(result.listUserMemberships?.count ?? undefined);
          setHasMore(result.listUserMemberships?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listUserMemberships?.pageInfo?.endCursor ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch user memberships')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, after, search, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a Relay/GraphQL UserMembershipFilterInput from the simplified filter interface
 */
function buildRelayFilter(
  filter?: UserMembershipQueryFilter
): UserMembershipFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: UserMembershipFilterInput = {};

  if (filter.userMembershipId) {
    relayFilter.id = { eq: filter.userMembershipId };
  }
  if (filter.membershipId) {
    relayFilter.membership = { id: { eq: filter.membershipId } };
  }
  if (filter.membershipOldId) {
    relayFilter.membershipOldId = { eq: filter.membershipOldId };
  }
  if (filter.userId) {
    relayFilter.userId = { eq: filter.userId };
  }
  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }

  // Status filters using server-computed boolean fields
  if (filter.active) {
    relayFilter.isActive = { eq: true };
  }
  if (filter.pending) {
    relayFilter.isPending = { eq: true };
  }
  if (filter.approved) {
    relayFilter.isApproved = { eq: true };
  }
  if (filter.rejected) {
    relayFilter.isRejected = { eq: true };
  }
  if (filter.canceled) {
    relayFilter.isCanceled = { eq: true };
  }
  if (filter.expired) {
    relayFilter.isExpired = { eq: true };
  }
  if (filter.notEnded) {
    relayFilter.canceledOn = { isNil: true };
    relayFilter.rejectedOn = { isNil: true };
    relayFilter.isExpired = { eq: false };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildRelayFilter };
